import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useApi } from '../auth/ApiProvider';
import { createPasskey, PasskeyError, passkeysSupported, type PasskeyFailure } from '../auth/webauthn';
import { useI18n } from '../i18n/I18nProvider';
import { CurrentPasswordField, Field, Input } from './Field';
import { Button, Card, Notice, Row, Stack } from './primitives';
import { ErrorState, LoadingState } from './states';

/** The words for a ceremony that produced nothing, by where it happened. */
const SIGN_IN_MESSAGES: Record<PasskeyFailure, string> = {
  cancelled: 'auth.passkey.cancelled',
  'already-registered': 'auth.passkey.failed',
  unsupported: 'auth.passkey.unsupported',
  failed: 'auth.passkey.failed',
};

const REGISTER_MESSAGES: Record<PasskeyFailure, string> = {
  cancelled: 'account.passkeys.cancelled',
  'already-registered': 'account.passkeys.alreadyRegistered',
  unsupported: 'account.passkeys.unsupported',
  failed: 'account.passkeys.failed',
};

/**
 * Shows why a passkey step failed.
 *
 * A failure on the device (the person closed the prompt, the browser has no WebAuthn) is worded here, since
 * no server wrote anything about it, and a cancellation is a quiet notice rather than an error: changing
 * your mind is not a fault. Anything the API refused is its own problem text, as everywhere else.
 */
export function PasskeyFailureNotice({ error, during }: { error: unknown; during: 'sign-in' | 'register' }) {
  const { t } = useI18n();

  if (error instanceof PasskeyError) {
    const key = (during === 'sign-in' ? SIGN_IN_MESSAGES : REGISTER_MESSAGES)[error.reason];
    return <Notice tone={error.reason === 'cancelled' ? 'info' : 'warning'}>{t(key)}</Notice>;
  }

  return <ErrorState error={error} />;
}

/**
 * The passkeys on the account screen: the ones registered, a way to add one on this device, and a way to
 * remove each.
 *
 * Registering needs a signed-in session and nothing more, as on the API. A browser without WebAuthn still
 * lists and removes them, so a lost phone's passkey can be taken off from any computer.
 */
export function PasskeysCard() {
  const api = useApi();
  const { t, formatDate } = useI18n();
  const queryClient = useQueryClient();
  const supported = passkeysSupported();

  const [deviceName, setDeviceName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [done, setDone] = useState<'saved' | 'removed' | null>(null);

  const passkeys = useQuery({ queryKey: ['account', 'passkeys'], queryFn: () => api.passkeys.list() });

  const add = useMutation({
    // The password first (Q25): a session left open must not be able to add a way in of its own.
    mutationFn: async () => {
      const stepUp = await api.passkeys.stepUp(password);
      const options = await api.passkeys.attestationOptions(stepUp.token);
      const credentialJson = await createPasskey(options);
      return api.passkeys.completeAttestation(credentialJson, deviceName.trim() || null);
    },
    onMutate: () => setDone(null),
    onSettled: () => setPassword(''),
    onSuccess: async () => {
      setDeviceName('');
      setDone('saved');
      await queryClient.invalidateQueries({ queryKey: ['account', 'passkeys'] });
    },
  });

  const remove = useMutation({
    mutationFn: (credentialId: string) => api.passkeys.remove(credentialId),
    onMutate: () => setDone(null),
    onSuccess: async () => {
      setDone('removed');
      await queryClient.invalidateQueries({ queryKey: ['account', 'passkeys'] });
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    const missing = password.length === 0;
    setPasswordError(missing ? t('auth.password.required') : null);
    if (!missing) add.mutate();
  }

  return (
    <Card title={t('account.passkeys')} hint={t('account.passkeys.hint')}>
      <Stack>
        {passkeys.isPending && <LoadingState />}
        {passkeys.isError && <ErrorState error={passkeys.error} onRetry={() => void passkeys.refetch()} />}
        {passkeys.data?.length === 0 && <p className="dg-muted">{t('account.passkeys.none')}</p>}

        {passkeys.data && passkeys.data.length > 0 && (
          <ul className="dg-stack dg-stack--tight" aria-label={t('account.passkeys')} style={{ listStyle: 'none', padding: 0 }}>
            {passkeys.data.map((passkey) => (
              <li key={passkey.credentialId}>
                <Row between>
                  <span className="dg-stack dg-stack--tight">
                    <span dir="auto">{passkey.name || t('account.passkeys.unnamed')}</span>
                    <span className="dg-muted dg-text-sm">
                      {t('account.passkeys.added', { date: formatDate(passkey.createdAt) })}
                    </span>
                  </span>
                  <Button
                    variant="ghost"
                    busy={remove.isPending && remove.variables === passkey.credentialId}
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(passkey.credentialId)}
                    aria-label={`${t('account.passkeys.remove')}: ${passkey.name || t('account.passkeys.unnamed')}`}
                  >
                    {t('account.passkeys.remove')}
                  </Button>
                </Row>
              </li>
            ))}
          </ul>
        )}

        {done && (
          <Notice tone="success">{t(done === 'saved' ? 'account.passkeys.saved' : 'account.passkeys.removed')}</Notice>
        )}
        {remove.isError && <ErrorState error={remove.error} />}
        {add.isError && <PasskeyFailureNotice error={add.error} during="register" />}

        {supported ? (
          <form onSubmit={submit} noValidate>
            <Stack>
              <Field label={t('account.passkeys.deviceName')} hint={t('account.passkeys.deviceName.hint')}>
                {(props) => (
                  <Input
                    {...props}
                    value={deviceName}
                    maxLength={64}
                    onChange={(event) => setDeviceName(event.target.value)}
                  />
                )}
              </Field>
              <CurrentPasswordField
                label={t('account.passkeys.currentPassword')}
                hint={t('account.passkeys.currentPasswordHint')}
                error={passwordError}
                value={password}
                onChange={setPassword}
              />
              <Row>
                <Button type="submit" variant="secondary" busy={add.isPending} busyLabel={t('action.saving')}>
                  {t('account.passkeys.add')}
                </Button>
              </Row>
            </Stack>
          </form>
        ) : (
          <Notice tone="info">{t('account.passkeys.unsupported')}</Notice>
        )}
      </Stack>
    </Card>
  );
}
